import React, { useEffect, useRef, useState } from 'react';
import { Popover, Typography, useMediaQuery } from '@mui/material';
import { hexToHsv, hsvToHex, normalizeHexColor, type HsvColor } from '../../../utils/color';

interface StudioColorPickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  color: string;
  title?: string;
  onClose: () => void;
  onChange: (color: string) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const StudioColorPicker: React.FC<StudioColorPickerProps> = ({ open, anchorEl, color, title, onClose, onChange }) => {
  const isMobile = useMediaQuery('(max-width:767px)');
  const [hsv, setHsv] = useState<HsvColor>(() => hexToHsv(normalizeHexColor(color) || '#f59e0b'));
  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const hsvRef = useRef(hsv);
  const onChangeRef = useRef(onChange);
  const wasOpenRef = useRef(false);
  const dragging = useRef<'sv' | 'hue' | null>(null);

  hsvRef.current = hsv;
  onChangeRef.current = onChange;

  // Only seed the picker from the incoming colour when it first opens. While the
  // panel is open the internal HSV is the single source of truth, so dragging into
  // dark/desaturated areas never round-trips through hex and loses the hue/position.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const nextHsv = hexToHsv(normalizeHexColor(color) || '#f59e0b');
      setHsv(nextHsv);
      hsvRef.current = nextHsv;
    }
    wasOpenRef.current = open;
  }, [open, color]);

  const commitHsv = (next: HsvColor) => {
    hsvRef.current = next;
    setHsv(next);
    onChangeRef.current(hsvToHex(next));
  };

  const updateSvFromEvent = (event: { clientX: number; clientY: number }) => {
    const node = svRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const s = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const v = clamp(1 - (event.clientY - rect.top) / rect.height, 0, 1);
    commitHsv({ ...hsvRef.current, s, v });
  };

  const updateHueFromEvent = (event: { clientX: number; clientY: number }) => {
    const node = hueRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const h = clamp(((event.clientX - rect.left) / rect.width) * 360, 0, 359.99);
    commitHsv({ ...hsvRef.current, h });
  };

  useEffect(() => {
    if (!open) return undefined;

    const pointFromTouch = (event: TouchEvent) => event.touches[0] || event.changedTouches[0];

    const onMove = (event: MouseEvent) => {
      if (dragging.current === 'sv') updateSvFromEvent(event);
      if (dragging.current === 'hue') updateHueFromEvent(event);
    };
    const onTouchMove = (event: TouchEvent) => {
      if (!dragging.current) return;
      event.preventDefault();
      const point = pointFromTouch(event);
      if (!point) return;
      if (dragging.current === 'sv') updateSvFromEvent(point);
      if (dragging.current === 'hue') updateHueFromEvent(point);
    };
    const onUp = () => {
      dragging.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [open]);

  const currentHex = hsvToHex(hsv);
  const pureHue = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  const picker = (
    <div className="studio-color-picker">
      {title && (
        <Typography variant="subtitle2" className="studio-color-picker-title">
          {title}
        </Typography>
      )}

      <div className="studio-color-picker-fields">
        <div
          ref={svRef}
          className="studio-color-picker-sv"
          style={{ backgroundColor: pureHue }}
          onMouseDown={event => {
            event.preventDefault();
            dragging.current = 'sv';
            updateSvFromEvent(event);
          }}
          onTouchStart={event => {
            dragging.current = 'sv';
            const point = event.touches[0];
            if (point) updateSvFromEvent(point);
          }}
        >
          <div className="studio-color-picker-sv-white" />
          <div className="studio-color-picker-sv-black" />
          <span
            className="studio-color-picker-thumb"
            style={{
              left: `${hsv.s * 100}%`,
              top: `${(1 - hsv.v) * 100}%`,
              background: currentHex
            }}
          />
        </div>

        <div
          ref={hueRef}
          className="studio-color-picker-hue"
          onMouseDown={event => {
            event.preventDefault();
            dragging.current = 'hue';
            updateHueFromEvent(event);
          }}
          onTouchStart={event => {
            dragging.current = 'hue';
            const point = event.touches[0];
            if (point) updateHueFromEvent(point);
          }}
        >
          <span
            className="studio-color-picker-hue-thumb"
            style={{ left: `${(hsv.h / 360) * 100}%`, background: pureHue }}
          />
        </div>
      </div>
    </div>
  );

  // Keep the picker inside the mobile tool sheet so its placement follows the
  // rainbow swatch and is not obscured by Android's system navigation area.
  if (isMobile) {
    return open ? <div className="studio-color-picker-inline">{picker}</div> : null;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableScrollLock
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: { className: 'studio-color-picker-paper', elevation: 0 },
        root: { className: 'studio-color-picker-root' }
      }}
    >
      {picker}
    </Popover>
  );
};

export default StudioColorPicker;
